import { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const AnalysisCandlestickChart = ({
  data,
  loading,
  peaksTroughs = [],
  isAdmin = false,
  showPeakNumbers = true,
  height = 400,
  visibleStartDate = null,
  visibleEndDate = null,
  showScrollHint = false,
  highlightRange = null,
  highlightStartDate = null,
  highlightEndDate = null,
  highlightUpColor = '#2563EB',
  highlightDownColor = '#1D4ED8',
  onRangeSelect = null,
  showTime = false,
}) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const markersApiRef = useRef(null);
  const selectionMarkersApiRef = useRef(null);
  const topHighlightSeriesRef = useRef(null);
  const bottomHighlightSeriesRef = useRef(null);
  const highlightedCandlesSeriesRef = useRef(null);
  const onRangeSelectRef = useRef(onRangeSelect);
  const selectionStateRef = useRef({ start: null, end: null, isSelecting: false });
  const [selectionDisplayState, setSelectionDisplayState] = useState({ start: null, end: null, isSelecting: false });
  useEffect(() => {
    onRangeSelectRef.current = onRangeSelect;
  }, [onRangeSelect]);

  const effectiveHighlightRange = highlightRange
    || (highlightStartDate && highlightEndDate
      ? { startDate: highlightStartDate, endDate: highlightEndDate }
      : null);
  const effectiveHighlightStartDate = effectiveHighlightRange?.startDate;
  const effectiveHighlightEndDate = effectiveHighlightRange?.endDate;

  useEffect(() => {
    if (!chartContainerRef.current || chartRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: 'solid', color: '#FFFFFF' },
        textColor: '#2E2620',
        fontFamily: 'Manrope, sans-serif',
      },
      localization: {
        locale: 'tr-TR',
        dateFormat: 'dd/MM/yyyy',
      },
      grid: {
        vertLines: { color: '#E6DCCF', style: 1 },
        horzLines: { color: '#E6DCCF', style: 1 },
      },
      crosshair: {
        vertLine: {
          color: '#C86F4A',
          width: 1,
          style: 2,
          labelBackgroundColor: '#C86F4A',
        },
        horzLine: {
          color: '#C86F4A',
          width: 1,
          style: 2,
          labelBackgroundColor: '#C86F4A',
        },
      },
      rightPriceScale: {
        borderColor: '#E6DCCF',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#E6DCCF',
        timeVisible: showTime,
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#6D7C3B',
      downColor: '#B04832',
      borderUpColor: '#6D7C3B',
      borderDownColor: '#B04832',
      wickUpColor: '#6D7C3B',
      wickDownColor: '#B04832',
    });

    const topHighlightSeries = chart.addSeries(LineSeries, {
      color: '#C86F4A',
      lineWidth: 2,
      lineStyle: 2,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const bottomHighlightSeries = chart.addSeries(LineSeries, {
      color: '#C86F4A',
      lineWidth: 2,
      lineStyle: 2,
      crosshairMarkerVisible: false,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const highlightedCandlesSeries = chart.addSeries(CandlestickSeries, {
      upColor: highlightUpColor,
      downColor: highlightDownColor,
      borderUpColor: highlightUpColor,
      borderDownColor: highlightDownColor,
      wickUpColor: highlightUpColor,
      wickDownColor: highlightDownColor,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    topHighlightSeriesRef.current = topHighlightSeries;
    bottomHighlightSeriesRef.current = bottomHighlightSeries;
    highlightedCandlesSeriesRef.current = highlightedCandlesSeries;

    chart.subscribeClick((param) => {
      if (!onRangeSelectRef.current || !param.time) return;
      const clickTime = typeof param.time === 'object'
        ? new Date(param.time.year, param.time.month - 1, param.time.day).getTime() / 1000
        : param.time;
      const currentState = selectionStateRef.current;
      if (!currentState.start || (currentState.start && currentState.end)) {
        selectionStateRef.current = { start: clickTime, end: null, isSelecting: true };
        setSelectionDisplayState({ start: clickTime, end: null, isSelecting: true });
      } else {
        const [finalStart, finalEnd] = currentState.start < clickTime
          ? [currentState.start, clickTime]
          : [clickTime, currentState.start];
        selectionStateRef.current = { start: finalStart, end: finalEnd, isSelecting: false };
        setSelectionDisplayState({ start: finalStart, end: finalEnd, isSelecting: false });
        onRangeSelectRef.current({ start: new Date(finalStart * 1000), end: new Date(finalEnd * 1000) });
      }
    });

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
        markersApiRef.current = null;
        selectionMarkersApiRef.current = null;
        topHighlightSeriesRef.current = null;
        bottomHighlightSeriesRef.current = null;
        highlightedCandlesSeriesRef.current = null;
      }
    };
  }, [height, highlightDownColor, highlightUpColor]);

  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({ timeScale: { timeVisible: showTime } });
  }, [showTime]);

  // Update data and markers
  useEffect(() => {
    if (candleSeriesRef.current && data && data.length > 0) {
      candleSeriesRef.current.setData(data);
      
      // Add markers for peaks and troughs using v5 API
      if (peaksTroughs && peaksTroughs.length > 0 && isAdmin) {
        const markers = peaksTroughs.map(pt => {
          // Find the timestamp for this date
          const dateStr = pt.date;
          const candle = data.find(c => {
            const candleDate = new Date(c.time * 1000).toISOString().split('T')[0];
            return candleDate === dateStr;
          });
          
          if (!candle) return null;
          
          return {
            time: candle.time,
            position: pt.point_type === 'tepe' ? 'aboveBar' : 'belowBar',
            color: pt.point_type === 'tepe' ? '#6D7C3B' : '#B04832',
            shape: pt.point_type === 'tepe' ? 'arrowDown' : 'arrowUp',
            text: showPeakNumbers ? `${pt.point_number}. ${pt.point_type === 'tepe' ? 'T' : 'D'}` : (pt.point_type === 'tepe' ? 'T' : 'D'),
          };
        }).filter(Boolean);
        
        // Use createSeriesMarkers API for v5
        if (markersApiRef.current) {
          markersApiRef.current.setMarkers(markers);
        } else if (markers.length > 0) {
          markersApiRef.current = createSeriesMarkers(candleSeriesRef.current, markers);
        }
      } else if (markersApiRef.current) {
        markersApiRef.current.setMarkers([]);
      }
      
      // Draw highlighted matched range on both charts (visual "box" style)
      if (topHighlightSeriesRef.current && bottomHighlightSeriesRef.current && effectiveHighlightStartDate && effectiveHighlightEndDate) {
        const startTime = data.find(c => new Date(c.time * 1000).toISOString().split('T')[0] >= effectiveHighlightStartDate)?.time;
        const endTime = [...data].reverse().find(c => new Date(c.time * 1000).toISOString().split('T')[0] <= effectiveHighlightEndDate)?.time;
        const inRange = startTime && endTime
          ? data.filter(c => c.time >= startTime && c.time <= endTime)
          : [];

        if (inRange.length > 0) {
          const high = Math.max(...inRange.map(c => c.high));
          const low = Math.min(...inRange.map(c => c.low));
          topHighlightSeriesRef.current.setData([
            { time: inRange[0].time, value: high },
            { time: inRange[inRange.length - 1].time, value: high },
          ]);
          bottomHighlightSeriesRef.current.setData([
            { time: inRange[0].time, value: low },
            { time: inRange[inRange.length - 1].time, value: low },
          ]);
          if (highlightedCandlesSeriesRef.current) {
            highlightedCandlesSeriesRef.current.setData(
              inRange.map(candle => ({
                time: candle.time,
                open: candle.open,
                high: candle.high,
                low: candle.low,
                close: candle.close,
              }))
            );
          }
        } else {
          topHighlightSeriesRef.current.setData([]);
          bottomHighlightSeriesRef.current.setData([]);
          if (highlightedCandlesSeriesRef.current) {
            highlightedCandlesSeriesRef.current.setData([]);
          }
        }
      } else if (topHighlightSeriesRef.current && bottomHighlightSeriesRef.current) {
        topHighlightSeriesRef.current.setData([]);
        bottomHighlightSeriesRef.current.setData([]);
        if (highlightedCandlesSeriesRef.current) {
          highlightedCandlesSeriesRef.current.setData([]);
        }
      }

      // Set visible range if specified, otherwise fit all content
      if (visibleStartDate && visibleEndDate && chartRef.current) {
        // Find the timestamps for start and end dates
        const startCandle = data.find(c => {
          const candleDate = new Date(c.time * 1000).toISOString().split('T')[0];
          return candleDate >= visibleStartDate;
        });
        const endCandle = [...data].reverse().find(c => {
          const candleDate = new Date(c.time * 1000).toISOString().split('T')[0];
          return candleDate <= visibleEndDate;
        });
        
        if (startCandle && endCandle) {
          chartRef.current.timeScale().setVisibleRange({
            from: startCandle.time,
            to: endCandle.time
          });
        } else {
          chartRef.current.timeScale().fitContent();
        }
      } else {
        chartRef.current?.timeScale().fitContent();
      }
    }
  }, [data, peaksTroughs, isAdmin, showPeakNumbers, visibleStartDate, visibleEndDate, effectiveHighlightStartDate, effectiveHighlightEndDate]);

  useEffect(() => {
    if (!candleSeriesRef.current || !data || data.length === 0) return;
    const markers = [];
    if (selectionDisplayState.start) {
      const c = data.find(d => d.time >= selectionDisplayState.start) || data[data.length - 1];
      if (c) markers.push({ time: c.time, position: 'belowBar', color: '#6D7C3B', shape: 'arrowUp', text: 'Başlangıç' });
    }
    if (selectionDisplayState.end && !selectionDisplayState.isSelecting) {
      const c = data.find(d => d.time >= selectionDisplayState.end) || data[data.length - 1];
      if (c) markers.push({ time: c.time, position: 'aboveBar', color: '#B04832', shape: 'arrowDown', text: 'Bitiş' });
    }
    if (markers.length > 0) {
      if (selectionMarkersApiRef.current) {
        selectionMarkersApiRef.current.setMarkers(markers);
      } else {
        selectionMarkersApiRef.current = createSeriesMarkers(candleSeriesRef.current, markers);
      }
    } else if (selectionMarkersApiRef.current) {
      selectionMarkersApiRef.current.setMarkers([]);
    }
  }, [selectionDisplayState, data]);

  const resetSelection = useCallback(() => {
    selectionStateRef.current = { start: null, end: null, isSelecting: false };
    setSelectionDisplayState({ start: null, end: null, isSelecting: false });
  }, []);

  const formatSelectionDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="relative">
      {onRangeSelect && (
        <div className="flex items-center justify-between mb-3 px-1 min-h-[32px]">
          <div className="flex items-center gap-4">
            {selectionDisplayState.isSelecting && selectionDisplayState.start && (
              <div className="flex items-center gap-2 text-sm animate-pulse">
                <span className="text-[#7A6A5C]">Başlangıç:</span>
                <span className="font-medium text-[#6D7C3B] px-2 py-1 bg-[#6D7C3B]/10 rounded">
                  {formatSelectionDate(selectionDisplayState.start)}
                </span>
                <span className="text-[#C86F4A] font-medium">→ Bitiş için grafikte tıklayın</span>
              </div>
            )}
            {!selectionDisplayState.isSelecting && selectionDisplayState.start && selectionDisplayState.end && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#7A6A5C]">Seçilen:</span>
                <span className="px-2 py-1 bg-[#6D7C3B]/10 rounded text-[#6D7C3B] font-medium">
                  {formatSelectionDate(selectionDisplayState.start)}
                </span>
                <span className="text-[#7A6A5C]">→</span>
                <span className="px-2 py-1 bg-[#B04832]/10 rounded text-[#B04832] font-medium">
                  {formatSelectionDate(selectionDisplayState.end)}
                </span>
                <span className="text-xs text-[#6D7C3B] ml-2">✓ tarihlere uygulandı</span>
              </div>
            )}
            {!selectionDisplayState.start && !selectionDisplayState.isSelecting && (
              <span className="text-sm text-[#A89F91]">
                Grafik üzerinde tıklayarak tarih aralığı seçebilirsiniz
              </span>
            )}
          </div>
          {(selectionDisplayState.start || selectionDisplayState.end) && (
            <Button variant="ghost" size="sm" onClick={resetSelection} className="text-[#7A6A5C] hover:text-[#2E2620] text-xs">
              Seçimi Sıfırla
            </Button>
          )}
        </div>
      )}
      <div
        ref={chartContainerRef}
        className="relative rounded-lg overflow-hidden border border-[#E6DCCF]"
        style={{ height: `${height}px`, cursor: onRangeSelect ? 'crosshair' : 'default' }}
      >
        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-[#C86F4A]" />
          </div>
        )}
      </div>
      
      {/* Scroll Hint */}
      {showScrollHint && (
        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-[#7A6A5C]">
          <span>← Grafiği kaydırarak kalıptan sonrasını görebilirsiniz →</span>
        </div>
      )}
      
    </div>
  );
};

export default AnalysisCandlestickChart;
